import { useState } from "react";
import type { ParticipationToday } from "@tih/shared";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { CopyText } from "../CopyText";
import { getAtPath } from "../../lib/siteCopyPaths";
import { ParticipationPanel } from "./ParticipationPanel";
import { ParticipationPromptContent } from "./ParticipationPromptContent";
import { EventPrepChecklist } from "./EventPrepChecklist";

type Props = {
  data: ParticipationToday;
  onUpdate: () => void;
};

export function ParticipationSpotlight({ data, onUpdate }: Props) {
  const { copy } = useSiteCopy();
  const [open, setOpen] = useState(false);
  const { spotlight, stats, registeredSeats, prepChecklist, featuredEvent, daysOut } = data;
  const dayLabel = spotlight.dayLabel ?? spotlight.title;

  const showPrepInline =
    spotlight.kind === "ACTION_PREP" ||
    (featuredEvent &&
      daysOut != null &&
      daysOut <= 14 &&
      daysOut >= 2 &&
      spotlight.family === "EVENT_PREP");

  return (
    <>
      <article className="participation-spotlight">
        <div className="participation-spotlight-meta">
          <span className="participation-spotlight-day">
            {String(getAtPath(copy, "home.participation.todayLabel") ?? "Today's invitation")}
            {dayLabel ? ` · ${dayLabel}` : ""}
          </span>
          {data.proximity !== "evergreen" && daysOut != null && daysOut >= 0 && (
            <span className="participation-spotlight-countdown">{daysOut}d</span>
          )}
        </div>
        <h2 className="sec-title sec-title-sm">{spotlight.title}</h2>
        {spotlight.subtitle && (
          <p className="sec-lede" style={{ opacity: 0.82, marginBottom: 24 }}>
            {spotlight.subtitle}
          </p>
        )}
        <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
          Respond <span className="arr">→</span>
        </button>
      </article>

      <ParticipationPanel
        open={open}
        onClose={() => setOpen(false)}
        title={spotlight.title}
        subtitle={spotlight.subtitle}
      >
        <ParticipationPromptContent
          prompt={spotlight}
          stats={stats}
          registeredSeats={registeredSeats}
          prepChecklist={prepChecklist}
          eventId={featuredEvent?.id ?? null}
          onUpdate={onUpdate}
        />
      </ParticipationPanel>

      {showPrepInline && featuredEvent && spotlight.kind !== "ACTION_PREP" && (
        <div className="participation-hub-prep-inline">
          <EventPrepChecklist eventId={featuredEvent.id} items={prepChecklist} />
        </div>
      )}
    </>
  );
}
